/* iter194 — Admin drawer for reviewing employee timesheet notes.
   ------------------------------------------------------------
   Contract:
     - `open`      : bool
     - `onClose`   : callback
     - `notes`     : array of {id, user_name, date, note, created_at, updated_at,
                     reviewed_at, reviewed_by_name}
     - `title`     : e.g. "Notes for Feb 8, 2026" or "Adam Cordle — Notes"
     - `onReviewed`: called after a note is marked reviewed so parent can refresh
   ------------------------------------------------------------ */

import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Check, X } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDT = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export const TimesheetNotesDrawer = ({ open, onClose, notes = [], title = 'Timesheet Notes', onReviewed }) => {
  const markReviewed = async (id) => {
    try {
      await axios.put(`${API}/admin/timesheet-notes/${id}/review`, {}, { withCredentials: true });
      toast.success('Marked reviewed');
      onReviewed?.();
    } catch { toast.error('Failed'); }
  };
  const undoReview = async (id) => {
    try {
      await axios.delete(`${API}/admin/timesheet-notes/${id}/review`, { withCredentials: true });
      toast.success('Review undone');
      onReviewed?.();
    } catch { toast.error('Failed'); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <SheetContent side="right" data-testid="notes-drawer" className="bg-[#0A0A0A] border-[#262626] text-white w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-[#D4AF37] flex items-center gap-2">
            <FileText className="w-4 h-4" /> {title}
          </SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-3">
          {notes.length === 0 ? (
            <p data-testid="notes-drawer-empty" className="text-[#A3A3A3] text-sm">No notes.</p>
          ) : notes.map((n) => (
            <div
              key={n.id}
              data-testid={`note-item-${n.id}`}
              className={`border rounded p-3 ${n.reviewed_at ? 'border-[#262626] bg-[#111]' : 'border-[#D4AF37]/40 bg-[#1a1505]'}`}
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <p className="text-white text-sm font-bold truncate">{n.user_name || 'Employee'}</p>
                  <p className="text-[#A3A3A3] text-[11px]">
                    {n.date} · submitted {fmtDT(n.created_at)}
                    {n.updated_at && n.updated_at !== n.created_at && ` · edited ${fmtDT(n.updated_at)}`}
                  </p>
                </div>
                {n.reviewed_at ? (
                  <div className="flex items-center gap-2">
                    <span data-testid={`note-reviewed-${n.id}`} className="text-green-400 text-[10px] uppercase tracking-wider bg-green-500/10 border border-green-500/40 px-2 py-0.5 rounded">
                      Reviewed by {n.reviewed_by_name || 'admin'} · {fmtDT(n.reviewed_at)}
                    </span>
                    <Button
                      data-testid={`undo-review-${n.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => undoReview(n.id)}
                      className="border-[#262626] text-[#A3A3A3] hover:bg-[#262626] h-7 px-2"
                      title="Undo review"
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    data-testid={`mark-reviewed-${n.id}`}
                    size="sm"
                    onClick={() => markReviewed(n.id)}
                    className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] h-7 px-2 font-bold"
                  >
                    <Check className="w-3.5 h-3.5 mr-1" /> Mark Reviewed
                  </Button>
                )}
              </div>
              <p className="mt-2 text-white text-sm border-l-2 border-[#D4AF37] pl-2 whitespace-pre-wrap" data-testid={`note-text-${n.id}`}>
                {n.note}
              </p>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default TimesheetNotesDrawer;
